/** @jsx jsx */

import type { ReactNode } from 'react';
import { Children, Fragment } from 'react';
import { Box } from '@balance-web/box';
import { jsx } from '@balance-web/core';
import { Flex } from '@balance-web/flex';
import type { StackProps } from '@balance-web/stack';
import { Stack } from '@balance-web/stack';
import type { TextProps } from '@balance-web/text';
import {
  DefaultTextPropsProvider,
  Text,
  useDefaultTextProps,
} from '@balance-web/text';
import { useTheme } from '@balance-web/theme';

const typeToElement = {
  bullet: 'ul',
  number: 'ol',
  alpha: 'ol',
} as const;

export type TextListProps = {
  children: StackProps['children'];
  size?: TextProps['size'];
  gap?: StackProps['gap'];
  color?: TextProps['color'];
  type?: keyof typeof typeToElement;
  data?: StackProps['data'];
};

export const TextList = ({
  children,
  size: sizeProp,
  color: colorProp,
  gap = 'small',
  type = 'bullet',
  data,
}: TextListProps) => {
  const { typography } = useTheme();
  const { size, color } = useDefaultTextProps({
    size: sizeProp,
    color: colorProp,
  });
  const listItems = Children.toArray(children);
  const lastNumberLength =
    type === 'number' ? listItems.length.toString().length : -1;

  return (
    <DefaultTextPropsProvider color={color} size={size}>
      <Stack as={typeToElement[type]} data={data} gap={gap}>
        {Children.map(listItems, (listItem, index) => {
          return (
            <Flex as="li">
              <Text color={color} size={size}>
                <Flex
                  aria-hidden
                  alignItems={type === 'bullet' ? 'center' : undefined}
                  height={`${typography.leading.base}em`}
                  userSelect="none"
                >
                  {(() => {
                    if (type === 'number') {
                      return (
                        <CharacterBullet length={lastNumberLength}>
                          {index + 1}
                        </CharacterBullet>
                      );
                    }

                    if (type === 'alpha') {
                      return (
                        <CharacterBullet>
                          {numberToAlpha(index + 1)}
                        </CharacterBullet>
                      );
                    }

                    return (
                      <Fragment>
                        {/** Better than messing around with line heights manually. */}
                        &nbsp;
                        <Bullet size={size} />
                      </Fragment>
                    );
                  })()}
                </Flex>
              </Text>

              <Box
                minWidth={0}
                paddingLeft={size === 'xsmall' ? 'xsmall' : 'small'}
                width="100%"
              >
                {listItem}
              </Box>
            </Flex>
          );
        })}
      </Stack>
    </DefaultTextPropsProvider>
  );
};

// Styled components
// ------------------------------

const charWidth = 0.4;
const minCharacterWidth = [`${1 + charWidth}ch`, `${2 + charWidth}ch`];
type CharacterBulletProps = { children: ReactNode; length?: number };
const CharacterBullet = ({ children, length = 1 }: CharacterBulletProps) => {
  return (
    <span
      css={{
        display: 'inline-block',
        minWidth:
          minCharacterWidth[length - 1] ??
          minCharacterWidth[minCharacterWidth.length - 1],
        marginRight: `${-charWidth}ch`,
      }}
    >
      {children}.
    </span>
  );
};

const textSizeToBullet = {
  xsmall: 3,
  small: 4,
  medium: 4,
  large: 6,
  xlarge: 6,
};

const Bullet = ({ size }: { size: NonNullable<TextProps['size']> }) => {
  const bulletSize = textSizeToBullet[size];
  return (
    <Box
      borderRadius="full"
      css={{ backgroundColor: 'currentColor' }}
      height={bulletSize}
      width={bulletSize}
    />
  );
};

// Utils
// ------------------------------

function numberToAlpha(inputNumber: number) {
  let returnValue = '';
  let counter = inputNumber;

  while (counter > 0) {
    const t = (counter - 1) % 26;
    returnValue = String.fromCharCode(97 + t) + returnValue;
    counter = ((counter - t) / 26) | 0;
  }

  return returnValue;
}
